(() => {
  const PRODUCT_WIDGET_SELECTOR = '[id*="trustify_reviews_app_product_block_"]';
  const PRODUCT_STARS_SELECTOR = '[id*="trustify_reviews_app_stars_block_"]';
  const CARD_WIDGET_SELECTOR = '[id*="trustify_reviews_app_stars_collection_block_"]';
  const GALLERY_CLASS = 'kinriot-review-media-gallery';
  let queued = false;
  let latestProductData = window.kinriotTrustifyCachedProductData || null;

  const toNumber = (value) => {
    const number = Number(String(value || '').replace(',', '.').replace(/[^0-9.]/g, ''));
    return Number.isFinite(number) ? number : 0;
  };

  const parseRating = (root) => {
    const averageNode = root.querySelector('.tr-review-stars__average, [class*="average-rating"], [class*="rating-average"]');
    const countNode = root.querySelector('.tr-review-stars__total-rating, [class*="review-count"], [class*="rating-count"]');
    let average = toNumber(averageNode?.textContent);
    let count = Math.round(toNumber(countNode?.textContent));

    if (!average || !count) {
      const text = root.textContent.replace(/\s+/g, ' ');
      const match = text.match(/([0-5](?:[.,]\d+)?)\s*\(\s*(\d+)\s*\)/);
      if (match) {
        average = toNumber(match[1]);
        count = Math.round(toNumber(match[2]));
      }
    }

    return average > 0 && count > 0 ? { average, count } : null;
  };

  const ratingStorageKey = (rating) => {
    const productId = rating?.dataset.productId;
    return productId ? 'kinriot-rating:' + productId : '';
  };

  const readStoredRating = (rating) => {
    const key = ratingStorageKey(rating);
    if (!key) return null;
    try {
      const data = JSON.parse(localStorage.getItem(key) || 'null');
      if (!data || Date.now() - Number(data.savedAt || 0) > 7 * 24 * 60 * 60 * 1000) return null;
      return data.average > 0 && data.count > 0 ? data : null;
    } catch (error) {
      return null;
    }
  };

  const writeStoredRating = (rating, data) => {
    const key = ratingStorageKey(rating);
    if (!key || !data) return;
    try {
      const current = JSON.parse(localStorage.getItem(key) || 'null');
      if (current && current.average === data.average && current.count === data.count) return;
      localStorage.setItem(key, JSON.stringify({
        average: data.average,
        count: data.count,
        savedAt: Date.now(),
      }));
    } catch (error) {
      // Storage can be unavailable in privacy modes; the rendered rating still works.
    }
  };

  const applyRating = (rating, data) => {
    if (!rating || !data) return;
    const fill = rating.querySelector('.kinriot-native-rating__fill');
    const average = rating.querySelector('.kinriot-native-rating__average');
    const count = rating.querySelector('.kinriot-native-rating__count');
    if (fill) fill.style.width = Math.max(0, Math.min(100, data.average * 20)) + '%';
    if (average) average.textContent = data.average.toFixed(1);
    if (count) count.textContent = '(' + data.count + ')';
    rating.hidden = false;
    rating.removeAttribute('aria-hidden');
    rating.setAttribute('aria-label', data.average.toFixed(1) + ' out of 5 stars from ' + data.count + ' reviews');
    writeStoredRating(rating, data);
  };

  const hydrateStoredRatings = () => {
    document.querySelectorAll('[data-kinriot-native-rating]').forEach((rating) => {
      const stored = readStoredRating(rating);
      const currentCount = Math.round(toNumber(rating.querySelector('.kinriot-native-rating__count')?.textContent));
      if (stored && stored.count >= currentCount) applyRating(rating, stored);
    });
  };

  const latestRating = () => {
    const statistic = latestProductData?.statistic;
    if (!statistic) return null;
    const average = toNumber(statistic.rating_avg);
    const count = Math.round(toNumber(statistic.total_rating));
    return average > 0 && count > 0 ? { average, count } : null;
  };

  const syncProductHeaderRatings = () => {
    let freshest = null;
    document.querySelectorAll(PRODUCT_STARS_SELECTOR).forEach((widget) => {
      const data = parseRating(widget);
      if (data && (!freshest || data.count >= freshest.count)) freshest = data;
    });
    if (freshest) {
      document.querySelectorAll('[data-kinriot-native-rating][data-rating-context="product"]').forEach((rating) => {
        applyRating(rating, freshest);
      });
    }

    const direct = latestRating();
    if (direct) {
      document.querySelectorAll('[data-kinriot-native-rating][data-rating-context="product"]').forEach((rating) => {
        applyRating(rating, direct);
      });
    }
  };

  const syncCardRatings = () => {
    document.querySelectorAll('product-card.kinriot-product-card--enhanced').forEach((card) => {
      const widget = card.querySelector(CARD_WIDGET_SELECTOR);
      const rating = card.querySelector('[data-kinriot-native-rating][data-rating-context="card"]');
      if (widget && rating) applyRating(rating, parseRating(widget));
    });
  };

  const imageSource = (image) => {
    const srcset = image.getAttribute('data-srcset') || image.getAttribute('srcset') || '';
    const srcsetFirst = srcset.split(',')[0]?.trim().split(/\s+/)[0] || '';
    const candidates = [
      image.dataset.kinriotOriginalSrc,
      image.getAttribute('data-src'),
      image.getAttribute('data-lazy-src'),
      image.getAttribute('data-original'),
      image.getAttribute('data-lazy'),
      srcsetFirst,
      image.currentSrc,
      image.getAttribute('src'),
      image.src,
    ];
    return candidates.find((source) => source && !String(source).startsWith('data:')) || '';
  };

  const isPlaceholderImage = (image, src) => {
    const descriptor = [
      src,
      image.alt,
      image.className,
      image.parentElement?.className,
    ].join(' ').toLowerCase();
    return /avatar|logo|icon|spinner|loader|loading|placeholder|skeleton|\.svg(?:\?|$)/.test(descriptor);
  };

  const resizeShopifyImage = (src, width = 240) => {
    try {
      const url = new URL(src, window.location.href);
      if (url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.shopifycdn.com')) {
        url.searchParams.set('width', String(width));
        return url.toString();
      }
    } catch (error) {
      return src;
    }
    return src;
  };

  const collectLatestReviewImages = () => {
    const seen = new Set();
    const images = [];
    (latestProductData?.reviews || []).forEach((review) => {
      (review.images || []).forEach((media) => {
        const src = media.thumbnail_url || media.image_url || media.image || '';
        const key = src.split('?')[0].replace(/_[0-9]+x[0-9]+(?=\.[a-z]+$)/i, '');
        if (!src || seen.has(key)) return;
        seen.add(key);
        images.push({
          image: null,
          src,
          key,
          reviewId: String(review.id || media.review_id || ''),
          reviewerName: review.reviewer_name || '',
          title: review.title || review.seller_title || '',
          alt: (review.reviewer_name ? review.reviewer_name + ' — ' : '') + 'customer review photo',
        });
      });
    });
    return images;
  };

  const mergeReviewImages = (...groups) => {
    const seen = new Set();
    return groups.flat().filter((entry) => {
      if (!entry?.src || seen.has(entry.key)) return false;
      seen.add(entry.key);
      return true;
    });
  };

  const findGalleryHeading = (root) => {
    const candidates = root.querySelectorAll('h1, h2, h3, h4, h5, p, span, div');
    return Array.from(candidates).find((element) => {
      if (element.closest('.' + GALLERY_CLASS)) return false;
      const text = element.textContent.trim().replace(/\s+/g, ' ');
      return /^Reviews with images$/i.test(text) || /^Photo reviews$/i.test(text);
    });
  };

  const collectReviewImages = (root) => {
    const seen = new Set();
    return Array.from(root.querySelectorAll('img'))
      .filter((image) => !image.closest('.' + GALLERY_CLASS))
      .map((image) => {
        const src = imageSource(image);
        const key = src.split('?')[0].replace(/_[0-9]+x[0-9]+(?=\.[a-z]+$)/i, '');
        return {
          image,
          src,
          key,
          reviewId: image.dataset.reviewId || '',
          reviewerName: '',
          title: '',
          alt: image.alt || 'Customer review photo',
        };
      })
      .filter(({ image, src, key }) => {
        if (!src || seen.has(key) || isPlaceholderImage(image, src)) return false;
        const width = image.naturalWidth || image.width || 0;
        const height = image.naturalHeight || image.height || 0;
        if (width && height && (width < 72 || height < 72)) return false;
        image.removeAttribute('data-kinriot-review-backdrop');
        image.closest('a')?.removeAttribute('data-kinriot-review-backdrop-link');
        image.loading = 'eager';
        image.decoding = 'async';
        if ('fetchPriority' in image) image.fetchPriority = 'low';
        seen.add(key);
        return true;
      });
  };

  const repairInlineReviewImages = (root, reviewImages) => {
    if (!reviewImages.length) return;
    let fallbackIndex = 0;

    root.querySelectorAll('.tr-review-box__item').forEach((item) => {
      const existing = Array.from(item.querySelectorAll('img')).find((image) => {
        const src = imageSource(image);
        return src && !isPlaceholderImage(image, src);
      });

      if (existing) {
        existing.removeAttribute('data-kinriot-review-backdrop');
        existing.closest('a')?.removeAttribute('data-kinriot-review-backdrop-link');
        existing.loading = 'eager';
        existing.decoding = 'async';
        return;
      }

      const media = item.querySelector(
        '.tr-review-media, .tr-review-media__item, .tr-image, [class*="review-media"], [class*="review_media"]'
      );
      if (!media || media.querySelector('.kinriot-inline-review-photo')) return;

      const reviewId = item.dataset.reviewId || media.dataset.reviewId || item.querySelector('[data-review-id]')?.dataset.reviewId || '';
      const itemText = item.textContent.trim().replace(/\s+/g, ' ').toLowerCase();
      let source = reviewId ? reviewImages.find((entry) => String(entry.reviewId) === String(reviewId)) : null;
      if (!source) {
        source = reviewImages.find((entry) => {
          const reviewer = String(entry.reviewerName || '').toLowerCase();
          const title = String(entry.title || '').toLowerCase();
          return reviewer && itemText.includes(reviewer) && (!title || itemText.includes(title));
        });
      }
      if (!source) source = reviewImages[fallbackIndex++];
      if (!source) return;

      const photo = document.createElement('img');
      photo.className = 'kinriot-inline-review-photo';
      photo.src = resizeShopifyImage(source.src, 240);
      photo.dataset.kinriotOriginalSrc = source.src;
      photo.alt = source.alt || source.image?.alt || 'Customer review photo';
      photo.loading = 'eager';
      photo.decoding = 'async';
      if ('fetchPriority' in photo) photo.fetchPriority = 'low';
      photo.addEventListener('error', () => {
        if (photo.src !== source.src) photo.src = source.src;
      }, { once: true });
      media.appendChild(photo);
    });
  };

  const updateProductRating = (root) => {
    let data = latestRating() || parseRating(root);
    if (!data) {
      const countMatch = root.innerText.match(/\((\d+)\s+reviews?\)/i);
      const rating = document.querySelector('[data-kinriot-native-rating][data-rating-context="product"]');
      const currentAverage = toNumber(rating?.querySelector('.kinriot-native-rating__average')?.textContent);
      if (countMatch && currentAverage) data = { average: currentAverage, count: Number(countMatch[1]) };
    }
    document.querySelectorAll('[data-kinriot-native-rating][data-rating-context="product"]').forEach((rating) => applyRating(rating, data));
  };

  const clearReviewBackground = (root) => {
    root.dataset.kinriotSolidReviewBackground = 'true';
    root.style.setProperty('background-color', '#f8f2e9', 'important');
    root.style.setProperty('background-image', 'none', 'important');

    root.querySelectorAll('[data-kinriot-review-backdrop]').forEach((element) => {
      element.removeAttribute('data-kinriot-review-backdrop');
    });
    root.querySelectorAll('[data-kinriot-review-backdrop-link]').forEach((element) => {
      element.removeAttribute('data-kinriot-review-backdrop-link');
    });

    root.querySelectorAll('[style*="background-image"], [class*="review-background"], [class*="review_background"], [class*="media-background"], [class*="media_background"], [class*="backdrop"]').forEach((element) => {
      element.style.setProperty('background-image', 'none', 'important');
      element.style.setProperty('background-color', 'transparent', 'important');
    });

    const rootBox = root.getBoundingClientRect();
    root.querySelectorAll('img').forEach((image) => {
      if (image.closest('.' + GALLERY_CLASS)) return;
      const box = image.getBoundingClientRect();
      const style = getComputedStyle(image);
      const parentClass = String(image.parentElement?.className || '').toLowerCase();
      const isClearlyOversized = box.width >= Math.max(620, rootBox.width * 0.7) && box.height >= 320;
      const isLayered = style.position === 'absolute' || style.position === 'fixed' || /background|backdrop|hero|cover/.test(parentClass);
      if (isClearlyOversized && isLayered) {
        image.dataset.kinriotReviewBackdrop = 'true';
        const imageLink = image.closest('a');
        if (imageLink && !imageLink.closest('.' + GALLERY_CLASS)) {
          imageLink.dataset.kinriotReviewBackdropLink = 'true';
        }
      }
    });
  };

  const hideNativeReviewImageStrip = (root) => {
    root.querySelectorAll('.tr-review-pin-photo__item').forEach((item) => {
      const strip = item.closest('.tr-display-flex.tr-flex-wrap') || item.parentElement;
      if (!strip || strip.closest('.' + GALLERY_CLASS)) return;
      if (strip.dataset.kinriotOriginalMediaGallery !== 'true') {
        strip.dataset.kinriotOriginalMediaGallery = 'true';
      }
      if (!strip.hidden) strip.hidden = true;
    });
  };

  const rebuildGallery = (root) => {
    clearReviewBackground(root);
    hideNativeReviewImageStrip(root);
    updateProductRating(root);
    const heading = findGalleryHeading(root);
    if (!heading) return;

    const reviewImages = mergeReviewImages(
      collectLatestReviewImages(),
      collectReviewImages(root)
    );
    repairInlineReviewImages(root, reviewImages);
    if (reviewImages.length < 1) return;

    let gallery = root.querySelector('.' + GALLERY_CLASS);
    if (!gallery) {
      gallery = document.createElement('div');
      gallery.className = GALLERY_CLASS;
      gallery.setAttribute('aria-label', 'All customer review photos');
      heading.insertAdjacentElement('afterend', gallery);
    }

    const signature = 'clean-v3|' + reviewImages.map(({ src }) => src).join('|');
    if (gallery.dataset.signature === signature) return;
    gallery.dataset.signature = signature;
    gallery.replaceChildren();

    reviewImages.forEach(({ image, src }) => {
      const link = document.createElement('a');
      link.href = src;
      link.target = '_blank';
      link.rel = 'noopener';
      link.setAttribute('aria-label', image?.alt || 'Open customer review photo');
      const clone = document.createElement('img');
      clone.src = resizeShopifyImage(src, 240);
      clone.dataset.kinriotOriginalSrc = src;
      clone.loading = 'eager';
      clone.decoding = 'async';
      if ('fetchPriority' in clone) clone.fetchPriority = 'low';
      clone.alt = image?.alt || 'Customer review photo';
      clone.addEventListener('error', () => {
        if (clone.src !== src) clone.src = src;
      }, { once: true });
      link.appendChild(clone);
      gallery.appendChild(link);
    });

    hideNativeReviewImageStrip(root);
  };

  const refresh = () => {
    queued = false;
    hydrateStoredRatings();
    syncProductHeaderRatings();
    syncCardRatings();
    document.querySelectorAll(PRODUCT_WIDGET_SELECTOR).forEach(rebuildGallery);
  };

  const queueRefresh = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(refresh);
  };

  const style = document.createElement('style');
  style.textContent = `
    .kinriot-rtw__rating-slot,
    .kinriot-int__rating-slot {
      position: absolute !important;
      width: 1px !important;
      min-width: 1px !important;
      max-width: 1px !important;
      height: 1px !important;
      min-height: 1px !important;
      max-height: 1px !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: hidden !important;
      clip: rect(0 0 0 0) !important;
      clip-path: inset(50%) !important;
      white-space: nowrap !important;
      pointer-events: none !important;
    }
    [data-kinriot-solid-review-background="true"],
    [data-kinriot-solid-review-background="true"] > div {
      background-color: #f8f2e9 !important;
      background-image: none !important;
    }
    [data-kinriot-solid-review-background="true"] [style*="background-image"],
    [data-kinriot-solid-review-background="true"] [class*="review-background"],
    [data-kinriot-solid-review-background="true"] [class*="review_background"],
    [data-kinriot-solid-review-background="true"] [class*="media-background"],
    [data-kinriot-solid-review-background="true"] [class*="media_background"],
    [data-kinriot-solid-review-background="true"] [class*="backdrop"],
    [data-kinriot-review-backdrop="true"] {
      background-image: none !important;
    }
    img[data-kinriot-review-backdrop="true"],
    [data-kinriot-review-backdrop-link="true"] { display: none !important; }
    [data-kinriot-original-media-gallery="true"],
    [data-kinriot-solid-review-background="true"] .tr-review-pin-photo > .tr-display-flex.tr-flex-wrap {
      display: none !important;
    }
    [data-kinriot-solid-review-background="true"] .tr-review-box__item img:not(.tr-reviewer__avatar),
    [data-kinriot-solid-review-background="true"] [class*="review-media"] img,
    [data-kinriot-solid-review-background="true"] [class*="review_media"] img,
    .kinriot-inline-review-photo {
      display: block !important;
      visibility: visible !important;
      opacity: 1 !important;
      max-width: 100% !important;
      object-fit: cover !important;
      background: transparent !important;
    }
    .kinriot-inline-review-photo {
      width: 72px !important;
      height: 72px !important;
      border-radius: 3px !important;
    }
    .kinriot-review-media-gallery {
      position: static !important;
      inset: auto !important;
      z-index: auto !important;
      display: flex !important;
      flex-wrap: wrap !important;
      gap: 10px !important;
      width: 100% !important;
      height: auto !important;
      min-height: 0 !important;
      margin: 14px 0 18px !important;
      padding: 0 !important;
      overflow: visible !important;
      background: transparent !important;
      transform: none !important;
    }
    .kinriot-review-media-gallery a {
      position: relative !important;
      inset: auto !important;
      z-index: auto !important;
      display: block !important;
      flex: 0 0 92px !important;
      width: 92px !important;
      min-width: 92px !important;
      max-width: 92px !important;
      height: 92px !important;
      min-height: 92px !important;
      max-height: 92px !important;
      aspect-ratio: 1 !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: hidden !important;
      border: 1px solid rgba(23, 19, 17, .12) !important;
      border-radius: 4px !important;
      background: #fff !important;
      transform: none !important;
    }
    .kinriot-review-media-gallery img {
      position: static !important;
      inset: auto !important;
      z-index: auto !important;
      display: block !important;
      width: 100% !important;
      min-width: 0 !important;
      max-width: 100% !important;
      height: 100% !important;
      min-height: 0 !important;
      max-height: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
      object-fit: cover !important;
      opacity: 1 !important;
      transform: none !important;
    }
    @media (max-width: 749px) {
      .kinriot-review-media-gallery {
        flex-wrap: nowrap;
        overflow-x: auto;
        padding-bottom: 4px;
      }
      .kinriot-review-media-gallery a {
        flex: 0 0 76px !important;
        width: 76px !important;
        min-width: 76px !important;
        max-width: 76px !important;
        height: 76px !important;
        min-height: 76px !important;
        max-height: 76px !important;
      }
    }
  `;
  document.head.appendChild(style);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', queueRefresh, { once: true });
  } else {
    queueRefresh();
  }

  const latestPromise = window.kinriotTrustifyProductPromise;
  if (latestPromise && typeof latestPromise.then === 'function') {
    latestPromise.then((data) => {
      if (!data) return;
      latestProductData = data;
      syncProductHeaderRatings();
      queueRefresh();
    });
  }

  document.addEventListener('load', (event) => {
    if (event.target instanceof HTMLImageElement && event.target.closest(PRODUCT_WIDGET_SELECTOR)) {
      queueRefresh();
    }
  }, true);

  new MutationObserver(queueRefresh).observe(document.documentElement, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ['src', 'srcset', 'data-src', 'data-lazy-src', 'data-original'],
  });
})();
